import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Badge, Modal, Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './style.css';

function CreateEmailCampaign({ user }) {
    const navigate = useNavigate();
    const { id: campaignId } = useParams();
    const isEditMode = Boolean(campaignId);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [breakingNewsArticles, setBreakingNewsArticles] = useState([]);
    const [users, setUsers] = useState([]);
    const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [showHtmlModal, setShowHtmlModal] = useState(false);

    const [campaignForm, setCampaignForm] = useState({
        article_id: '',
        subject: '',
        preview_text: '',
        email_body: '',
        campaign_type: 'breaking_news', // Always breaking news
        status: 'draft',
        scheduled_at: '',
        selected_users: []
    });

    useEffect(() => {
        fetchBreakingNewsArticles();
        fetchUsers();
        if (isEditMode) {
            fetchCampaignData();
        }
    }, []);

    const fetchBreakingNewsArticles = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/admin/articles', {
                params: {
                    article_type: 'breaking-news',
                    status: 'published',
                    limit: 50
                },
                headers: { Authorization: `Bearer ${user.token}` }
            });

            console.log('Articles API Response:', response.data);

            if (response.data.status) {
                // Handle different response structures
                const articles = response.data.data?.articles || response.data.data || response.data.articles || [];
                console.log('Parsed articles:', articles);
                setBreakingNewsArticles(articles);
            } else {
                console.warn('API returned status false:', response.data);
                setBreakingNewsArticles([]);
            }
        } catch (error) {
            console.error('Error fetching breaking news:', error);
            console.error('Error details:', error.response?.data);
            setError('Failed to load articles. Please check console for details.');
            setBreakingNewsArticles([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get('/admin/users', {
                headers: { Authorization: `Bearer ${user.token}` }
            });
            if (response.data.status) {
                setUsers(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchCampaignData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`/admin/email-campaigns/${campaignId}`, {
                headers: { Authorization: `Bearer ${user.token}` }
            });

            if (response.data.status) {
                const campaign = response.data.data.campaign;
                const recipients = response.data.data.recipients || [];

                // Set campaign form data
                setCampaignForm({
                    article_id: campaign.article_id || '',
                    subject: campaign.subject || '',
                    preview_text: campaign.preview_text || '',
                    email_body: campaign.email_body || '',
                    campaign_type: campaign.campaign_type || 'breaking_news',
                    status: campaign.status || 'draft',
                    scheduled_at: campaign.scheduled_at || '',
                    selected_users: []
                });

                // Set selected users from recipients
                if (recipients.length > 0) {
                    setSelectedUsers(recipients.map(r => r.user_id));
                }

                // Set editor state from email body
                if (campaign.email_body) {
                    try {
                        const blocksFromHTML = convertFromHTML(campaign.email_body);
                        const contentState = ContentState.createFromBlockArray(
                            blocksFromHTML.contentBlocks,
                            blocksFromHTML.entityMap
                        );
                        const newEditorState = EditorState.createWithContent(contentState);
                        setEditorState(newEditorState);
                    } catch (error) {
                        console.error('Error converting HTML to editor state:', error);
                    }
                }

                // Find and set the selected article
                if (campaign.article_id && breakingNewsArticles.length > 0) {
                    const article = breakingNewsArticles.find(a => a.id == campaign.article_id);
                    if (article) {
                        setSelectedArticle(article);
                    }
                }
            } else {
                setError('Failed to load campaign data');
                navigate('/admin-panel/email-marketing');
            }
        } catch (error) {
            console.error('Error fetching campaign:', error);
            setError('Failed to load campaign data');
            setTimeout(() => navigate('/admin-panel/email-marketing'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleArticleSelect = (e) => {
        const articleId = e.target.value;
        const article = breakingNewsArticles.find(a => a.id == articleId);

        if (article) {
            setSelectedArticle(article);

            // Create email body with article excerpt and read more link
            const articleUrl = `${window.location.origin}/article/${article.slug || article.id}`;

            // Get only the first sentence for preview (up to first period, question mark, or exclamation)
            // Parse Draft.js JSON content from database and extract plain text
            let fullText = '';
            try {
                const contentData = typeof article.content === 'string'
                    ? JSON.parse(article.content)
                    : article.content;

                // Extract text from Draft.js blocks
                if (contentData.blocks && Array.isArray(contentData.blocks)) {
                    fullText = contentData.blocks
                        .map(block => block.text || '')
                        .join(' ')
                        .trim();
                } else {
                    // Fallback: if it's HTML, strip tags
                    fullText = String(article.content).replace(/<[^>]+>/g, '');
                }
            } catch (error) {
                console.error('Error parsing article content:', error);
                // Fallback: treat as HTML and strip tags
                fullText = String(article.content).replace(/<[^>]+>/g, '');
            }


            let previewText = fullText;

            // Find the first sentence ending
            const sentenceEndings = ['. ', '! ', '? '];
            let firstEndingIndex = -1;

            for (let ending of sentenceEndings) {
                const index = fullText.indexOf(ending);
                if (index !== -1 && (firstEndingIndex === -1 || index < firstEndingIndex)) {
                    firstEndingIndex = index + 1; // Include the punctuation
                }
            }

            // If we found a sentence ending, use only the first sentence
            if (firstEndingIndex > 0 && firstEndingIndex < fullText.length) {
                previewText = fullText.substring(0, firstEndingIndex).trim();
            } else if (previewText.length > 150) {
                // Fallback: if no sentence ending found, limit to 150 chars
                previewText = fullText.substring(0, 150).trim() + '...';
            }

            // Clean up any extra whitespace and ensure it's plain text
            previewText = previewText.replace(/\s+/g, ' ').trim();

            // const emailBody = `
            //     <h2 style="color: #0050f0; margin-bottom: 20px;">${article.title}</h2>
            //     <p style="font-size: 16px; line-height: 1.6; margin-bottom: 10px;">
            //         ${previewText} <a href="${articleUrl}" style="color: #0050f0; text-decoration: none; font-weight: 600;">Read More</a>
            //     </p>
            //     <p style="color: #6c757d; font-size: 14px; margin-top: 30px; border-top: 1px solid #e0e0e0; padding-top: 20px;">
            //         Stay informed with Inside Injuries - Your trusted source for breaking sports injury news.
            //     </p>
			// `;
            const unsubscribeUrl = `https://www.insideinjuries.com/unsubscribe?email=${encodeURIComponent(user.email)}`;

            // Check if article has a feature image
            const featureImageHtml = article.thumbnail 
                ? `<tr>
                    <td style="padding:20px 30px 0 30px;">
                        <img src="${article.thumbnail}" 
                             alt="${article.title}" 
                             width="560"
                             height="auto"
                             style="width:100%;max-width:560px;height:auto;display:block;border:1px solid #e0e0e0;" />
                    </td>
                   </tr>`
                : '';
            console.log('Feature image HTML:', featureImageHtml);

            const emailBody = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>${campaignForm.subject || 'Inside Injuries'}</title>
            </head>
            <body style="margin:0;padding:0;background-color:#ffffff;font-family:Arial, Helvetica, sans-serif;">
                <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin:0;padding:0;background-color:#ffffff;">
                    <tr>
                        <td align="center" style="padding:20px 0;background-color:#ffffff;">

                        <!-- MAIN CONTAINER -->
                        <table width="600" border="0" cellspacing="0" cellpadding="0" bgcolor="#eff2f8"
                            style="border:1px solid #dddddd;margin:0 auto;">

                            <!-- LOGO -->
                            <tr>
                            <td align="center" style="padding:30px 20px 20px 20px;background-color:#eff2f8;">
                                <img
                                src="https://inside-content.s3.us-east-1.amazonaws.com/inside.png"
                                width="220"
                                height="auto"
                                alt="Inside Injuries"
                                style="display:block;border:none;outline:none;text-decoration:none;"
                                />
                            </td>
                            </tr>

                            ${featureImageHtml}

                            <!-- TITLE -->
                            <tr>
                            <td style="padding:25px 30px 15px 30px;background-color:#eff2f8;">
                                <h1 style="margin:0;font-size:24px;line-height:30px;font-weight:bold;color:#000000;font-family:Arial, Helvetica, sans-serif;">
                                ${article.title}
                                </h1>
                            </td>
                            </tr>

                            <!-- CONTENT -->
                            <tr>
                            <td style="padding:0 30px 25px 30px;background-color:#eff2f8;">
                                <p style="margin:0;font-size:16px;line-height:24px;color:#333333;font-family:Arial, Helvetica, sans-serif;">
                                ${previewText}
                                </p>
                            </td>
                            </tr>

                            <!-- READ MORE BUTTON -->
                            <tr>
                            <td align="center" style="padding:0 30px 30px 30px;background-color:#eff2f8;">
                                <table border="0" cellspacing="0" cellpadding="0">
                                    <tr>
                                        <td align="center" style="background-color:#0050f0;border-radius:6px;">
                                            <a href="${articleUrl}"
                                            target="_blank"
                                            style="display:inline-block;padding:12px 28px;color:#ffffff;font-size:15px;font-weight:bold;text-decoration:none;font-family:Arial, Helvetica, sans-serif;">
                                            Read Full Article →
                                            </a>
                                        </td>
                                    </tr>
                                </table>
                            </td>
                            </tr>

                            <!-- DISCLAIMER -->
                            <tr>
                            <td style="padding:20px 30px;border-top:1px solid #dddddd;background-color:#f4f6fb; text-align: center;">
                                <p style="margin:0;font-size:13px;line-height:20px;color:#6c757d;font-family:Arial, Helvetica, sans-serif;">
                                You are receiving this email because you subscribed to injury alerts
                                from Inside Injuries.
                                </p>
                            </td>
                            </tr>
                            
                            <!-- FOOTER -->
                            <tr>
                            <td align="center" style="padding:20px 30px;background-color:#f4f6fb;">
                                <p style="margin:0;font-size:13px;line-height:20px;color:#6c757d;font-family:Arial, Helvetica, sans-serif;">
                                © ${new Date().getFullYear()} Inside Injuries<br/>
                                Trusted source for breaking sports injury news<br/>
                                <a href="https://www.insideinjuries.com"
                                    target="_blank"
                                    style="color:#0050f0;text-decoration:underline;font-family:Arial, Helvetica, sans-serif;">
                                    www.insideinjuries.com
                                </a>
                                </p>
                            </td>
                            </tr> 

                            <!-- UNSUBSCRIBE -->
                            <tr>
                            <td align="center" style="padding:15px 30px 30px 30px;background-color:#f4f6fb;">
                                <a href="${unsubscribeUrl}"
                                target="_blank"
                                style="font-size:12px;color:#666666;text-decoration:underline;font-family:Arial, Helvetica, sans-serif;">
                                Unsubscribe
                                </a>
                            </td>
                            </tr>

                        </table>

                        </td>
                    </tr>
                </table>
            </body>
            </html>
            `;


                // console.log('Generated emailBody HTML:', emailBody);

                // Set the email body directly without converting through Draft.js
                // This preserves the table structure for email compatibility
                setCampaignForm({
                    ...campaignForm,
                    article_id: articleId,
                    subject: `Breaking News: ${article.title}`,
                    preview_text: article.meta_desc || article.title.substring(0, 150),
                    email_body: emailBody
                });

                // Set editor state to show a simplified preview (but don't use it for actual email)
                try {
                    const simpleContent = `
                        <h2>${article.title}</h2>
                        <p>${previewText}</p>
                        <p><a href="${articleUrl}">Read Full Article →</a></p>
                    `;
                    const blocksFromHTML = convertFromHTML(simpleContent);
                    const contentState = ContentState.createFromBlockArray(
                        blocksFromHTML.contentBlocks,
                        blocksFromHTML.entityMap
                    );
                    const newEditorState = EditorState.createWithContent(contentState);
                    setEditorState(newEditorState);
                } catch (error) {
                    console.error('Error setting editor state:', error);
                    setEditorState(EditorState.createEmpty());
                }
            } else {
                setSelectedArticle(null);
                setEditorState(EditorState.createEmpty());
                setCampaignForm({
                    ...campaignForm,
                    article_id: articleId,
                    subject: '',
                    preview_text: '',
                    email_body: ''
                });
            }
        };

    const uploadImage = async (file) => {
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size should be less than 5MB');
            return;
        }

        try {
            // Upload to server
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post('/admin/upload/image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`
                }
            });

            if (response.data.status && response.data.data.url) {
                return { data: { link: response.data.data.url } };
            } else {
                setError('Failed to upload image');
                return;
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload image');
            return;
        }
    };

    const handleEditorStateChange = (newEditorState) => {
        setEditorState(newEditorState);
        
        // Only update email body if user has manually edited the content
        // This prevents overriding the properly formatted email template
        if (selectedArticle && campaignForm.email_body.includes('<!DOCTYPE html>')) {
            // User is working with auto-generated template, don't override with Draft.js HTML
            // The editor is just for preview purposes
            return;
        }
        
        const htmlContent = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
        setCampaignForm({
            ...campaignForm,
            email_body: htmlContent
        });
    };

    const handleToggleUserSelection = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const handleSelectAllUsers = () => {
        if (selectAll) {
            setSelectedUsers([]);
            setSelectAll(false);
        } else {
            const activeUsers = getFilteredUsers().filter(u => u.email_breaking_news !== false && u.email_verified === 1);
            setSelectedUsers(activeUsers.map(u => u.id));
            setSelectAll(true);
        }
    };

    const getFilteredUsers = () => {
        if (!userSearchTerm) return users;
        return users.filter(u =>
            u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            u.full_name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            u.first_name?.toLowerCase().includes(userSearchTerm.toLowerCase())
        );
    };

    const handleSubmit = async (e, action = 'draft') => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Frontend Validation
        if (!isEditMode && !campaignForm.article_id) {
            setError('Please select an article');
            return;
        }

        if (!campaignForm.subject || campaignForm.subject.trim().length < 3) {
            setError('Subject must be at least 3 characters long');
            return;
        }

        if (campaignForm.subject.trim().length > 255) {
            setError('Subject must not exceed 255 characters');
            return;
        }

        if (!campaignForm.email_body || campaignForm.email_body.trim().length < 10) {
            setError('Email body must be at least 10 characters long');
            return;
        }

        if (campaignForm.preview_text && campaignForm.preview_text.trim().length > 500) {
            setError('Preview text must not exceed 500 characters');
            return;
        }

        setLoading(true);

        try {
            // Prepare payload
            const payload = {
                subject: campaignForm.subject.trim(),
                preview_text: campaignForm.preview_text.trim() || null,
                email_body: campaignForm.email_body,
                status: 'draft'
            };

            let response;
            let finalCampaignId = campaignId;

            if (isEditMode) {
                // Update existing campaign
                response = await axios.put(`/admin/email-campaigns/${campaignId}`, payload, {
                    headers: { Authorization: `Bearer ${user.token}` }
                });
            } else {
                // Create new campaign
                payload.article_id = parseInt(campaignForm.article_id);
                payload.campaign_type = 'breaking_news';
                payload.scheduled_at = null;
                payload.selected_users = selectedUsers.length > 0 ? selectedUsers : [];

                response = await axios.post('/admin/email-campaigns', payload, {
                    headers: { Authorization: `Bearer ${user.token}` }
                });

                if (response.data.status) {
                    finalCampaignId = response.data.data.campaign_id;
                }
            }

            if (response.data.status) {
                // If action is send, immediately send the campaign
                if (action === 'send') {
                    const sendResponse = await axios.post(`/admin/email-campaigns/${finalCampaignId}/send`, {}, {
                        headers: { Authorization: `Bearer ${user.token}` }
                    });

                    if (sendResponse.data.status) {
                        setSuccess(`Campaign sent successfully to ${sendResponse.data.data.total_sent} recipients!`);
                    } else {
                        setError(`Campaign ${isEditMode ? 'updated' : 'created'} but failed to send: ` + sendResponse.data.message);
                    }
                } else {
                    setSuccess(isEditMode ? 'Campaign updated successfully!' : 'Campaign saved as draft!');
                }

                // Redirect after 2 seconds
                setTimeout(() => {
                    navigate('/admin-panel/email-marketing');
                }, 2000);
            } else {
                setError(response.data.message || `Failed to ${isEditMode ? 'update' : 'create'} campaign`);
            }
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} campaign:`, error);
            setError(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} campaign`);
        } finally {
            setLoading(false);
        }
    };

    const activeUsersCount = users.filter(u => u.email_breaking_news !== false && u.email_verified === 1).length;

    return (
        <Container fluid className="create-email-campaign py-4">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="mb-1">{isEditMode ? 'Edit Email Campaign' : 'Create Email Campaign'}</h2>
                            <p className="text-muted mb-0">{isEditMode ? 'Update your campaign details' : 'Create and schedule breaking news campaigns'}</p>
                        </div>
                        <Button
                            variant="outline-secondary"
                            onClick={() => navigate('/admin-panel/email-marketing')}
                        >
                            ← Back to Campaigns
                        </Button>
                    </div>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" onClose={() => setSuccess('')} dismissible>
                    {success}
                </Alert>
            )}

            <Row>
                <Col lg={8}>
                    <Card className="campaign-form-card">
                        <Card.Body>
                            <h5 className="mb-4">Campaign Details</h5>
                            <Form onSubmit={(e) => handleSubmit(e, 'draft')}>
                                {/* Article Selection */}
                                <Form.Group className="mb-4">
                                    <Form.Label>Select Breaking News Article {!isEditMode && '*'}</Form.Label>
                                    <Form.Select
                                        value={campaignForm.article_id}
                                        onChange={handleArticleSelect}
                                        required={!isEditMode}
                                        size="lg"
                                        disabled={isEditMode || loading || breakingNewsArticles.length === 0}
                                    >
                                        <option value="">
                                            {loading ? 'Loading articles...' :
                                                breakingNewsArticles.length === 0 ? 'No breaking news articles available' :
                                                    'Choose an article...'}
                                        </option>
                                        {breakingNewsArticles.map((article) => (
                                            <option key={article.id} value={article.id}>
                                                {article.title}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        {isEditMode
                                            ? 'Article selection is locked in edit mode'
                                            : breakingNewsArticles.length > 0
                                                ? `${breakingNewsArticles.length} published breaking news article(s) available`
                                                : 'Only published breaking news articles are shown'}
                                    </Form.Text>
                                </Form.Group>

                                {/* Email Subject */}
                                <Form.Group className="mb-4">
                                    <Form.Label>Email Subject *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="lg"
                                        value={campaignForm.subject}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                                        placeholder="Breaking News: ..."
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        This will be the subject line of your email
                                    </Form.Text>
                                </Form.Group>

                                {/* Preview Text */}
                                <Form.Group className="mb-4">
                                    <Form.Label>Preview Text</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={campaignForm.preview_text}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, preview_text: e.target.value })}
                                        placeholder="Short preview text shown in email clients"
                                        maxLength={150}
                                    />
                                    <div className="d-flex justify-content-between">
                                        <Form.Text className="text-muted">
                                            This appears in the inbox preview
                                        </Form.Text>
                                        <Form.Text className="text-muted">
                                            {campaignForm.preview_text.length}/150 characters
                                        </Form.Text>
                                    </div>
                                </Form.Group>

                                {/* Email Body Editor */}
                                <Form.Group className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="mb-0">Email Body *</Form.Label>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            type="button"
                                            onClick={() => setShowHtmlModal(true)}
                                            disabled={!campaignForm.email_body}
                                        >
                                            View HTML
                                        </Button>
                                    </div>
                                    <div className="email-editor-wrapper">
                                        <Editor
                                            editorState={editorState}
                                            onEditorStateChange={handleEditorStateChange}
                                            placeholder="Select an article above to auto-populate email content..."
                                            toolbar={{
                                                options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'image', 'history'],
                                                inline: {
                                                    options: ['bold', 'italic', 'underline']
                                                },
                                                blockType: {
                                                    inDropdown: true,
                                                    options: ['Normal', 'H2', 'H3', 'H4']
                                                },
                                                fontSize: {
                                                    options: [12, 14, 16, 18, 20, 24]
                                                },
                                                list: {
                                                    options: ['unordered', 'ordered']
                                                },
                                                textAlign: {
                                                    options: ['left', 'center', 'right']
                                                },
                                                image: {
                                                    uploadCallback: uploadImage,
                                                    alt: { present: false, mandatory: false },
                                                    previewImage: true,
                                                    inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
                                                    defaultSize: {
                                                        height: 'auto',
                                                        width: '100%',
                                                    },
                                                }
                                            }}
                                        />
                                    </div>
                                    <Form.Text className="text-muted">
                                        <strong>Note:</strong> When you select an article, a professional email template is automatically generated. 
                                        The editor above shows a preview for reference, but the actual email sent will use the properly formatted template with table structure for maximum email client compatibility.
                                    </Form.Text>
                                </Form.Group>

                                {/* Action Buttons */}
                                <div className="d-flex gap-3 mt-5 justify-content-end">
                                        <Button
                                            variant="outline-secondary"
                                            size="lg"
                                            onClick={() => navigate('/admin-panel/email-marketing')}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            type="submit"
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : isEditMode ? 'Update Campaign' : 'Save as Draft'}
                                        </Button>
                                        {!isEditMode && (
                                            <Button
                                                variant="success"
                                                size="lg"
                                                onClick={(e) => handleSubmit(e, 'send')}
                                                disabled={loading}
                                            >
                                                {loading ? 'Sending...' : 'Create & Send Now'}
                                            </Button>
                                        )}
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Sidebar */}
                <Col lg={4}>
                    {/* Recipients Card */}
                    <Card className="mb-4 recipients-card">
                        <Card.Body>
                            <h5 className="mb-3">Recipients</h5>
                            <div className="recipients-info mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Total Users:</span>
                                    <Badge bg="secondary">{users.length}</Badge>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Active Subscribers:</span>
                                    <Badge bg="success">{activeUsersCount}</Badge>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>Selected:</span>
                                    <Badge bg="primary">{selectedUsers.length || 'All Active'}</Badge>
                                </div>
                            </div>
                            <Button
                                variant="outline-primary"
                                className="w-100"
                                onClick={() => setShowUserSelectionModal(true)}
                            >
                                Select Specific Users
                            </Button>
                        </Card.Body>
                    </Card>

                    {/* Tips Card */}
                    <Card className="tips-card">
                        <Card.Body>
                            <h6 className="mb-3">Tips</h6>
                            <ul className="tips-list">
                                <li>Use compelling subject lines to increase open rates</li>
                                <li>Preview text should complement the subject</li>
                                <li>Test with a small group before sending to all</li>
                                <li>Breaking news should be sent immediately for urgency</li>
                                <li>You can edit the email content using the rich text editor</li>
                            </ul>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* HTML Preview Modal */}
            <Modal
                show={showHtmlModal}
                onHide={() => setShowHtmlModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Email HTML Source</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Form.Control
                        as="textarea"
                        rows={20}
                        value={campaignForm.email_body || ''}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHtmlModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* User Selection Modal */}
            <Modal
                show={showUserSelectionModal}
                onHide={() => setShowUserSelectionModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>Select Recipients</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Search users by email or name..."
                            value={userSearchTerm}
                            onChange={(e) => setUserSearchTerm(e.target.value)}
                            size="lg"
                        />
                    </div>

                    <div className="mb-3 d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <Form.Check
                            type="checkbox"
                            label={`Select All Active Users (${getFilteredUsers().filter(u => u.email_breaking_news !== false && u.email_verified === 1).length})`}
                            checked={selectAll}
                            onChange={handleSelectAllUsers}
                            className="fw-bold"
                        />
                        <Badge bg="primary" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                            {selectedUsers.length} selected
                        </Badge>
                    </div>

                    <Table hover responsive>
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '60px' }}>Select</th>
                                <th>Email</th>
                                <th>Name</th>
                                <th style={{ width: '120px' }}>Email Verified</th>
                                <th style={{ width: '120px' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getFilteredUsers().length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted py-4">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                getFilteredUsers().map((user) => (
                                    <tr key={user.id} className={user.email_breaking_news === false || user.email_verified === 0 ? 'table-secondary' : ''}>
                                        <td>
                                            <Form.Check
                                                type="checkbox"
                                                checked={selectedUsers.includes(user.id)}
                                                onChange={() => handleToggleUserSelection(user.id)}
                                                disabled={user.email_breaking_news === false || user.email_verified === 0}
                                            />
                                        </td>
                                        <td>{user.email}</td>
                                        <td>{user.full_name || user.first_name || '-'}</td>
                                        <td>
                                            {user.email_verified === 1 ? (
                                                <Badge bg="success">Verified</Badge>
                                            ) : (
                                                <Badge bg="warning">Not Verified</Badge>
                                            )}
                                        </td>
                                        <td>
                                            {user.email_breaking_news === false ? (
                                                <Badge bg="secondary">Disabled</Badge>
                                            ) : user.email_verified === 0 ? (
                                                <Badge bg="warning">Not Verified</Badge>
                                            ) : (
                                                <Badge bg="success">Active</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowUserSelectionModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => setShowUserSelectionModal(false)}>
                        Confirm Selection ({selectedUsers.length})
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

const mapStateToProps = (state) => ({
    user: state.user
});

export default connect(mapStateToProps)(CreateEmailCampaign);
